import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Gauge, Fuel, Cog, MapPin, Calendar, Palette, MessageCircle, Phone } from "lucide-react";
import api, { formatPrice, formatKm, waLink, BUSINESS } from "../lib/api";
import ImageSlider from "../components/ImageSlider";

export default function CarDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/cars/${id}`)
      .then((r) => {
        setCar(r.data);
        document.title = `${r.data.year} ${r.data.name} — Faisal Car Bazar`;
      })
      .catch(() => navigate("/inventory"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="p-20 text-center text-[#A0A0A0]">Loading…</div>;
  if (!car) return null;

  const enquiry = waLink(
    `Hello Faisal Car Bazar, I'm interested in the ${car.year} ${car.name} (${formatPrice(car.price)}). Please share more details.`,
  );

  const specs = [
    { icon: Calendar, label: "Year", value: car.year },
    { icon: Gauge, label: "Kilometers", value: formatKm(car.kilometers) },
    { icon: Fuel, label: "Fuel", value: car.fuel_type },
    { icon: Cog, label: "Transmission", value: car.transmission },
    { icon: Palette, label: "Color", value: car.color || "—" },
    { icon: MapPin, label: "Location", value: car.location },
  ];

  return (
    <div data-testid="car-detail-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <Link
        to="/inventory"
        data-testid="back-to-inventory"
        className="inline-flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-[#D4AF37] mb-8"
      >
        <ArrowLeft size={16} /> Back to Inventory
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="rounded-2xl overflow-hidden">
            <ImageSlider images={car.images} alt={car.name} testId="detail-slider" aspect="aspect-[4/3]" />
          </div>
          <div className="grid grid-cols-5 gap-2 mt-3">
            {car.images.slice(0, 5).map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`thumb ${i + 1}`}
                className="aspect-square object-cover rounded-lg border border-white/5"
              />
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{car.brand}</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#F5F5DC] mt-2">{car.name}</h1>
          <p className="text-[#A0A0A0]">{car.model}</p>
          <div className="luxe-divider" />
          <div className="font-display text-4xl text-[#DC143C]">{formatPrice(car.price)}</div>
          {car.status === "sold" && (
            <span className="inline-block mt-3 px-4 py-1 rounded-full bg-[#DC143C] text-white text-xs uppercase tracking-[0.25em]">
              Sold
            </span>
          )}

          <div className="grid grid-cols-2 gap-3 mt-8">
            {specs.map((s) => (
              <div key={s.label} className="premium-card p-4">
                <s.icon size={16} className="text-[#D4AF37] mb-2" />
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0]">{s.label}</div>
                <div className="text-[#F5F5DC] text-sm mt-1">{s.value}</div>
              </div>
            ))}
          </div>

          {car.description && (
            <div className="mt-8">
              <h3 className="font-display text-xl text-[#F5F5DC] mb-3">Highlights</h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">{car.description}</p>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              data-testid="detail-whatsapp"
              href={enquiry}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ background: "#25D366", borderColor: "#25D366" }}
            >
              <MessageCircle size={16} /> WhatsApp Enquiry
            </a>
            <a
              href={`tel:${BUSINESS.phone}`}
              data-testid="detail-call"
              className="btn-secondary"
            >
              <Phone size={16} /> Call Dealer
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
